import { GetStaticPaths, GetStaticProps } from 'next';

import { KnowledgeCategoryPage } from '../../../components/KnowledgeArticle/KnowledgeCategoryPage';
import { KnowledgeCentreWithSearch } from '../../../components/KnowledgeArticle/KnowledgeCentreWithSearch';
import {
  getKnowledgeArticleBySlug,
  getKnowledgeArticles,
  getKnowledgeCategoryBySlug,
} from '../../../utils/knowledge/knowledgeArticles';
import {
  isKnowledgeLocale,
  KnowledgeLocale,
  resolveKnowledgeLocale,
} from '../../../utils/knowledge/knowledgeCopy';
import { KnowledgeChannel } from '../../../utils/knowledge/types';
import { getKnowledgeMinimalPageProps } from '../../../utils/knowledge/getKnowledgeMinimalPageProps';
import { NEXT_ISR_REVALIDATE_SECONDS } from '../../../utils/isr';
import { ServiceOSGlobalSettings } from '../../../utils/getServiceOSGlobalSettings';

const CHANNEL: KnowledgeChannel = 'hero';

interface HeroCategoryPageProps {
  locale: KnowledgeLocale;
  categoryName: string;
  articles: { urlName: string; title: string }[];
  topArticles: { urlName: string; title: string }[];
  serviceOS: ServiceOSGlobalSettings;
}

const HeroCategoryPage = ({ locale, categoryName, articles, topArticles }: HeroCategoryPageProps) => {
  const allArticles = getKnowledgeArticles(locale, CHANNEL);

  return (
    <KnowledgeCentreWithSearch
      channel={CHANNEL}
      locale={locale}
      title={`${categoryName} | Urban Hero help`}
      articles={allArticles}
    >
      <KnowledgeCategoryPage
        locale={locale}
        channel={CHANNEL}
        categoryName={categoryName}
        articles={articles}
        topArticles={topArticles}
      />
    </KnowledgeCentreWithSearch>
  );
};

export default HeroCategoryPage;

export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => ({
  paths: [],
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<HeroCategoryPageProps> = async ({ params, locale }) => {
  const resolvedLocale = resolveKnowledgeLocale(locale);

  if (!isKnowledgeLocale(resolvedLocale) || !params?.slug) {
    return { notFound: true };
  }

  const category = getKnowledgeCategoryBySlug(String(params.slug), resolvedLocale, CHANNEL);

  if (!category) {
    return { notFound: true };
  }

  const articles = category.articleSlugs
    .map((slug) => {
      const article = getKnowledgeArticleBySlug(slug, resolvedLocale, CHANNEL);
      return article ? { urlName: article.urlName, title: article.title } : null;
    })
    .filter(Boolean) as { urlName: string; title: string }[];

  const allArticles = getKnowledgeArticles(resolvedLocale, CHANNEL);
  const topArticles = allArticles.slice(0, 5).map(({ urlName, title }) => ({ urlName, title }));

  const minimalProps = await getKnowledgeMinimalPageProps(resolvedLocale);

  return {
    props: {
      locale: resolvedLocale,
      categoryName: category.name,
      articles,
      topArticles,
      ...minimalProps,
    },
    revalidate: NEXT_ISR_REVALIDATE_SECONDS,
  };
};
